

import { Pencil, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ChevronDown } from "lucide-react";

export default function ViewItemModal({
  viewModalItem,
  setViewModalItem,
  viewModalTab,
  setViewModalTab,
  editModalOpen,
  setEditModalOpen,
  fetchRacks,
  getSubCategories,
  setSubCategories,
  subCategories,
  rackList,
  editableDetails,
  setEditableDetails,
  handleVariantStockToggle,
  handleVariantVisibleToggle,
  optimisticStocks = {},
  optimisticVisible = {},
  handleDetailChange,
  previewImage,
  handleCatalogImageChange,
  handleSaveCatalog,
  availability,
  handleAvailabilityChange,
  handleSaveAvailability,
  selectedPrinter,
  setSelectedPrinter,
  printerList,
  handleSavePrinter,
  handleAddPrinter,
  getPrinters,
  savingCatalog,
}) {
  const navigate = useNavigate();

  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [activePriceIndex, setActivePriceIndex] = useState(0);

const [showSubCategoryDropdown, setShowSubCategoryDropdown] =
  useState(false);

  const selectedPrice =
    viewModalItem?.prices?.[activePriceIndex] ||
    viewModalItem?.prices?.[0] ||
    {};

  const [printerForm, setPrinterForm] = useState({
    printerName: "",
    printerAlias: "",
    deviceName: "",
    deviceNumber: "",
  });

  const syncEditableFromPriceRow = (
    idx,
    sourcePrices,
    baseItem = viewModalItem,
  ) => {
    const p = sourcePrices?.[idx] || {};

    setEditableDetails((prev) => ({
      ...(prev || {}),

      selectedPriceIndex: idx,

      item_id: baseItem?.item_id || prev?.item_id || "",
      item_name: baseItem?.item_name || prev?.item_name || "",
      item_name_trans: baseItem?.item_name_trans ?? "",
      item_description:
        baseItem?.item_description || prev?.item_description || "",
      sub_category: baseItem?.sub_category || prev?.sub_category || "",
      rackNumber: baseItem?.rackNumber || prev?.rackNumber || "",
      expiryDate: baseItem?.expiryDate || prev?.expiryDate || "",

      // IMPORTANT SELECTED PRICE ROW IDENTIFIERS
      id: p.id || prev?.id || "",
      sizeId: p.size_id || p.sizeId || prev?.sizeId || "",

      // VARIANT / SIZE
      size: p.size || "",
      variantName: p.variantName || p.size || "",
      variantId: p.variantId || p.size_id || p.sizeId || "",

      // PRICES
      taxPercentage: p.item_tax ?? p.taxPercentage ?? "",
      sellingPrice: p.price ?? "",
      homeDeliveryPrice: p.home_delivery_price ?? p.homeDeliveryPrice ?? "",
      takeawayPrice: p.takeaway_price ?? p.takeawayPrice ?? "",
      diningPrice: p.dining_price ?? p.diningPrice ?? "",
      roomOrder: p.room_order_price ?? p.roomOrder ?? "",
      carOrder: p.car_order_price ?? p.carOrder ?? "",
    }));
  };

  useEffect(() => {
    if (!viewModalItem) return;

    setActivePriceIndex(0);
    syncEditableFromPriceRow(0, viewModalItem.prices, viewModalItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewModalItem]);

  const handleOpenEditMode = async (idx = 0) => {
    setActivePriceIndex(idx);
    syncEditableFromPriceRow(idx, viewModalItem.prices, viewModalItem);

    await fetchRacks();

    const subs = await getSubCategories("");
    setSubCategories(subs);

    setEditModalOpen(true);
  };

  const handlePrinterChange = (e) => {
    const { name, value } = e.target;
    setPrinterForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveNewPrinter = async () => {
    try {
      const payload = {
        printerName: printerForm.printerName,
        printerAlias: printerForm.printerAlias,
        deviceName: printerForm.deviceName,
        deviceNumber: printerForm.deviceNumber,
      };

      const res = await handleAddPrinter(payload);

      if (res) {
        toast.success("Printer added!");
        setShowPrinterModal(false);

        await getPrinters();
      }
    } catch (err) {
      toast.error("Failed to add printer");
    }
  };

  if (!viewModalItem) return null;

  return (
    <>

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1">
        <div className="w-[1100px] max-w-[95vw] h-[92vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden montserrat-font-1">
        <div className="sticky top-0 z-30 bg-white">
          {/* HEADER */}
          <div className="flex items-start justify-between gap-4 border-b px-6 py-4 montserrat-font-1">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {viewModalItem.item_name}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Item ID:{" "}
                {editableDetails?.item_id || viewModalItem.item_id || "-"}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {viewModalTab === "details" && (
                <button
                  onClick={async () => {
                    if (editModalOpen) {
                      setEditModalOpen(false);
                    } else {
                      await handleOpenEditMode(0);
                    }
                  }}
                  className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-100"
                >
                  <Pencil size={15} />
                  {editModalOpen ? "Cancel Edit" : "Edit"}
                </button>
              )}

              <button
                onClick={() => setViewModalItem(null)}
                className="rounded-lg px-3 py-2 text-xl text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
          </div>
          </div>

          {/* TABS */}
          <div className="flex gap-2 border-b px-6 pt-3 montserrat-font-1">
            {["details", "availability", "printer"].map((tab) => (
              <button
                key={tab}
                onClick={() => setViewModalTab(tab)}
                className={`px-4 py-2 text-sm font-medium transition ${
                  viewModalTab === tab
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

         {/* BODY */}
<div className="flex-1 overflow-y-auto p-6 montserrat-font-1">
            {/* ================= DETAILS TAB - VIEW MODE ================= */}
            {viewModalTab === "details" && !editModalOpen && (
              <>
                <div className="mb-4 flex items-center justify-between montserrat-font-1">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Item Overview
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                      Review item details, prices, variants and stock
                      information.
                    </p>
                  </div>
                  {/* 
                  <button
                    onClick={() => handleOpenEditMode(0)}
                    className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-100"
                  >
                    <Pencil size={14} /> Edit
                  </button> */}
                </div>

                {/* IMAGE + DETAILS */}
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-[260px_1fr_1fr]">
                  {/* IMAGE CARD */}
                  <div className="rounded-2xl border bg-gray-50 p-3 shadow-sm">
                    <img
                      src={viewModalItem.photo || "/placeholder.png"}
                      alt={viewModalItem.item_name}
                      className="h-[230px] w-full rounded-xl border bg-white object-cover"
                    />

                    <div className="mt-3 text-center">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          selectedPrice.inStock === "Y"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {selectedPrice.inStock === "Y"
                          ? "In Stock"
                          : "Out of Stock"}
                      </span>
                    </div>

                    <div className="mt-4 rounded-xl border bg-white p-3">
                      <p className="mb-1 text-xs font-medium text-gray-500">
                        Description
                      </p>
                      <p className="text-sm leading-6 text-gray-700">
                        {viewModalItem.item_description?.trim()
                          ? viewModalItem.item_description
                          : "No description available for this item."}
                      </p>
                    </div>
                  </div>

                  {/* BASIC DETAILS */}
                  <div className="rounded-2xl border bg-white p-4 text-sm shadow-sm">
                    <h4 className="mb-4 font-semibold text-gray-900">
                      Basic Details
                    </h4>

                    <div className="space-y-4">
                      <div>
                        <p className="text-xs text-gray-500">Item Name</p>
                        <p className="font-semibold text-gray-900">
                          {viewModalItem.item_name || "-"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">Category</p>
                        <p className="font-medium text-gray-900">
                          {viewModalItem.category_name || "-"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">Expiry Date</p>
                        <p className="font-medium text-gray-900">
                          {editableDetails?.expiryDate ||
                            viewModalItem.expiryDate ||
                            "-"}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">
                          SubCat: {viewModalItem.sub_category || "-"}
                        </span>

                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">
                          Rack: {viewModalItem.rackNumber || "-"}
                        </span>

                        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700">
                          Variant: {selectedPrice.variantName || "-"}
                        </span>

                        <span className="rounded-full border bg-white px-3 py-1 text-xs text-gray-700">
                          Tax: {selectedPrice.item_tax ?? "-"}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* SELECTED PRICE */}
                  <div className="rounded-2xl border bg-white p-4 text-sm shadow-sm montserrat-font-1">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          Selected Price
                        </h4>
                        <p className="mt-1 text-xs text-gray-500">
                          Current active variant row
                        </p>
                      </div>

                      <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                        {selectedPrice.size ||
                          selectedPrice.variantName ||
                          "Default"}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {[
                        ["Selling", selectedPrice.price],
                        ["Home Delivery", selectedPrice.home_delivery_price],
                        ["Takeaway", selectedPrice.takeaway_price],
                        ["Dining", selectedPrice.dining_price],
                        ["Room Order", selectedPrice.room_order_price],
                        ["Car Order", selectedPrice.car_order_price],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          className="flex items-center justify-between border-b pb-2 last:border-b-0 last:pb-0"
                        >
                          <span className="text-gray-500">{label}</span>
                          <span className="font-semibold text-gray-900">
                            {value ? `₹${value}` : "-"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ALL SIZE / VARIANT ROWS */}
                {Array.isArray(viewModalItem.prices) &&
                  viewModalItem.prices.length > 0 && (
                    <div className="mt-6 montserrat-font-1">
                      <div className="mb-3 flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            All Size / Variant Rows
                          </h4>
                          <p className="mt-1 text-xs text-gray-500">
                            Open any row separately, review all prices quickly,
                            and switch editing easily.
                          </p>
                        </div>

                        <span className="text-xs text-gray-500">
                          {viewModalItem.prices.length}{" "}
                          {viewModalItem.prices.length === 1 ? "row" : "rows"}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {viewModalItem.prices.map((p, i) => {
                          const isActive = i === activePriceIndex;

                          return (
                            <div
                              key={i}
                              className={`rounded-2xl border p-4 text-sm shadow-sm transition ${
                                isActive
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 bg-white hover:border-blue-200"
                              }`}
                            >
                              <div className="mb-3 flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {p.size || p.variantName || `Row ${i + 1}`}
                                  </div>

                                  <div className="mt-1 text-xs text-gray-500">
                                    Variant: {p.variantName || p.size || "-"}
                                  </div>
                                </div>

                                <div className="flex flex-col items-end gap-2">
                                  <span className="rounded-full border bg-white px-2 py-1 text-xs text-gray-700">
                                    {p.item_tax ?? "-"}% tax
                                  </span>

                                  {isActive && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2 py-1 text-xs font-semibold text-white">
                                      <CheckCircle2 size={12} />
                                      Active
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="space-y-2">
                                {[
                                  ["Selling", p.price],
                                  ["Home Delivery", p.home_delivery_price],
                                  ["Takeaway", p.takeaway_price],
                                  ["Dining", p.dining_price],
                                  ["Room Order", p.room_order_price],
                                  ["Car Order", p.car_order_price],
                                ].map(([label, value]) => (
                                  <div
                                    key={label}
                                    className="flex items-center justify-between border-b pb-1 last:border-b-0 last:pb-0"
                                  >
                                    <span className="text-gray-500">
                                      {label}
                                    </span>
                                    <span className="font-semibold text-gray-900">
                                      {value ? `₹${value}` : "-"}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {(() => {
                                const variantId = String(
                                  p.id || p.row_id || p.size_id || "",
                                );

                                const isStockOn =
                                  optimisticStocks[variantId] !== undefined
                                    ? optimisticStocks[variantId] === "Y"
                                    : p.inStock === "Y";

                                const isVisibleOn =
                                  optimisticVisible[variantId] !== undefined
                                    ? optimisticVisible[variantId] === "Y"
                                    : p.visibility === "Y";

                                return (
                                  <div className="mt-4 border-t pt-3">
                                    <div className="mb-2 flex items-center justify-between">
                                      <span
                                        className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                                          isStockOn
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                        }`}
                                      >
                                        {isStockOn ? "Stock ON" : "Stock OFF"}
                                      </span>

                                      <span
                                        className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                                          isVisibleOn
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-gray-100 text-gray-600"
                                        }`}
                                      >
                                        {isVisibleOn
                                          ? "Visible ON"
                                          : "Visible OFF"}
                                      </span>
                                    </div>

                                    <div className="flex items-center justify-between gap-3">
                                      {/* Stock Toggle */}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleVariantStockToggle?.(
                                            viewModalItem,
                                            p,
                                          );
                                        }}
                                        className={`flex items-center gap-2 text-[11px] font-semibold ${
                                          isStockOn
                                            ? "text-green-700"
                                            : "text-red-600"
                                        }`}
                                      >
                                        <span
                                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
                                            isStockOn
                                              ? "bg-green-500"
                                              : "bg-gray-300"
                                          }`}
                                        >
                                          <span
                                            className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${
                                              isStockOn
                                                ? "translate-x-4"
                                                : "translate-x-1"
                                            }`}
                                          />
                                        </span>
                                        Stock
                                      </button>

                                      {/* Visible Toggle */}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleVariantVisibleToggle?.(
                                            viewModalItem,
                                            p,
                                          );
                                        }}
                                        className={`flex items-center gap-2 text-[11px] font-semibold ${
                                          isVisibleOn
                                            ? "text-blue-700"
                                            : "text-gray-500"
                                        }`}
                                      >
                                        <span
                                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
                                            isVisibleOn
                                              ? "bg-blue-500"
                                              : "bg-gray-300"
                                          }`}
                                        >
                                          <span
                                            className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${
                                              isVisibleOn
                                                ? "translate-x-4"
                                                : "translate-x-1"
                                            }`}
                                          />
                                        </span>
                                        Visible
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}

                              <button
                                onClick={() => handleOpenEditMode(i)}
                                className={`mt-4 w-full rounded-lg px-3 py-2 text-sm font-medium transition ${
                                  isActive
                                    ? "bg-blue-600 text-white"
                                    : "border border-blue-500 text-blue-600 hover:bg-blue-50"
                                }`}
                              >
                                {isActive ? "Edit Active Row" : "Edit This Row"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </>
            )}

            {/* ================= DETAILS TAB - EDIT MODE ================= */}
            {viewModalTab === "details" && editModalOpen && (
              <div className="montserrat-font-1">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Edit Item Details
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                      Row {Number(activePriceIndex) + 1} of{" "}
                      {viewModalItem.prices?.length || 0}
                    </p>
                  </div>

                  {/* <button
                    onClick={() => setEditModalOpen(false)}
                    className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-100"
                  >
                    <Pencil size={14} />
                    Cancel
                  </button> */}
                </div>

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[240px_1fr]">
                  {/* LEFT IMAGE */}
                  <div className="rounded-2xl border bg-white p-4 shadow-sm">
                    <img
                      src={
                        previewImage ||
                        viewModalItem.photo ||
                        "/placeholder.png"
                      }
                      alt={editableDetails.item_name || viewModalItem.item_name}
                      className="h-[220px] w-full rounded-xl border object-cover"
                    />

                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-800">
                        Update Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCatalogImageChange}
                        className="mt-2 w-full rounded-lg border px-3 py-2 text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500 montserrat-font-2">
                        Choose a new image to override the current one.
                      </p>
                    </div>
                  </div>

                  {/* RIGHT FORM */}
                  <div className="rounded-2xl border bg-white p-4 shadow-sm montserrat-font-1">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">
                          Item & Variant Information
                        </h4>
                        <p className="mt-1 text-xs text-gray-500">
                          Select a price row and edit only that variant row.
                        </p>
                      </div>

                      <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">
                        {editableDetails.size ||
                          selectedPrice.size ||
                          selectedPrice.variantName ||
                          "(no size)"}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {/* ITEM NAME */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Item Name
                        </label>
                        <input
                          name="item_name"
                          value={editableDetails.item_name || ""}
                          onChange={handleDetailChange}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

{/* TAG NAME */}
<div>
  <label className="text-sm font-medium text-gray-800">
    Tag Name
  </label>

  <input
    name="item_name_trans"
    value={editableDetails.item_name_trans || ""}
    onChange={handleDetailChange}
    placeholder="Enter Tag Name"
    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
  />
</div>

                      {/* DESCRIPTION */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Description
                        </label>
                        <input
                          name="item_description"
                          value={editableDetails.item_description || ""}
                          onChange={handleDetailChange}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* SUB CATEGORY */}
                      <div className="relative">
  <label className="text-sm font-medium text-gray-800">
    Sub-category
  </label>

  <div
    onClick={() => setShowSubCategoryDropdown((prev) => !prev)}
    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm text-gray-700 bg-white flex items-center justify-between cursor-pointer"
  >
    <span>
      {subCategories.find(
        (sc) =>
          sc.subCategory === editableDetails.sub_category ||
          sc.category === editableDetails.sub_category,
      )?.subCategory ||
        subCategories.find(
          (sc) =>
            sc.subCategory === editableDetails.sub_category ||
            sc.category === editableDetails.sub_category,
        )?.category ||
        "Select Subcategory"}
    </span>

    <span
      className={`transition-transform duration-200 ${
        showSubCategoryDropdown ? "rotate-180" : ""
      }`}
    >
      <ChevronDown size={18} />
    </span>
  </div>

  {showSubCategoryDropdown && (
    <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
      {subCategories.length === 0 ? (
        <div className="px-3 py-2 text-gray-500">
          No Subcategories
        </div>
      ) : (
        subCategories.map((sc) => (
          <div
            key={sc.id}
            onClick={() => {
              setEditableDetails({
                ...editableDetails,
                sub_category:
                  sc.subCategory || sc.category || "",
                categoryId: sc.id || "",
                category: sc.category || "",
              });

              setShowSubCategoryDropdown(false);
            }}
            className="px-3 py-2 cursor-pointer hover:bg-green-100"
          >
            {sc.subCategory || sc.category}
          </div>
        ))
      )}
    </div>
  )}
</div>

                      {/* RACK */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Rack Number
                        </label>
                        <select
                          name="rackNumber"
                          value={editableDetails.rackNumber || ""}
                          onChange={handleDetailChange}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        >
                          <option value="">Select Rack</option>
                          {rackList?.map((rack) => {
                            const tags = rack.tags?.join(", ");
                            return (
                              <option key={rack.id} value={rack.rackNumber}>
                                {`Rack ${rack.rackNumber} - ${rack.category}${
                                  tags ? ` (${tags})` : ""
                                }`}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {/* SELECTED SIZE / PRICE ROW */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Selected Size / Price Row
                        </label>

                        <select
                          value={activePriceIndex}
                          onChange={(e) => {
                            const idx = Number(e.target.value);
                            setActivePriceIndex(idx);
                            syncEditableFromPriceRow(
                              idx,
                              viewModalItem.prices,
                              viewModalItem,
                            );
                          }}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        >
                          {(viewModalItem.prices || []).map((p, i) => (
                            <option key={i} value={i}>
                              {`${i + 1}. ${
                                p.size || p.variantName || "(no size)"
                              } — ₹${p.price || 0}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* TAX */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Tax Percentage
                        </label>
                        <select
                          name="taxPercentage"
                          value={editableDetails.taxPercentage ?? ""}
                          onChange={handleDetailChange}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        >
                          <option value="">Select Tax</option>
                          <option value="0">0%</option>
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                          <option value="28">28%</option>
                        </select>
                      </div>

                      {/* VARIANT */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Variant
                        </label>
                        <input
                          name="variantName"
                          value={editableDetails.variantName || ""}
                          onChange={handleDetailChange}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* SIZE */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Size
                        </label>
                        <input
                          name="size"
                          value={editableDetails.size || ""}
                          onChange={handleDetailChange}
                          placeholder="Size"
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* SIZE ID */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Size ID
                        </label>
                        <input
                          name="sizeId"
                          value={editableDetails.sizeId || ""}
                          onChange={handleDetailChange}
                          placeholder="Size ID"
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* EXPIRY DATE */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Expiry Date
                        </label>
                        <input
                          name="expiryDate"
                          type="date"
                          value={editableDetails.expiryDate || ""}
                          onChange={handleDetailChange}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>
                    </div>

                    <hr className="my-5" />

                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">
                          Edit Active Size / Variant Prices
                        </h4>
                        <p className="mt-1 text-xs text-gray-500">
                          These prices belong to the selected row only.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {/* SELLING PRICE */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Selling Price
                        </label>
                        <input
                          name="sellingPrice"
                          value={editableDetails.sellingPrice || ""}
                          onChange={handleDetailChange}
                          placeholder="Selling Price"
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* HOME DELIVERY PRICE */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Home Delivery Price
                        </label>
                        <input
                          name="homeDeliveryPrice"
                          value={editableDetails.homeDeliveryPrice || ""}
                          onChange={handleDetailChange}
                          placeholder="Home Delivery Price"
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* TAKEAWAY PRICE */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Takeaway Price
                        </label>
                        <input
                          name="takeawayPrice"
                          value={editableDetails.takeawayPrice || ""}
                          onChange={handleDetailChange}
                          placeholder="Takeaway Price"
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* DINING PRICE */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Dining Price
                        </label>
                        <input
                          name="diningPrice"
                          value={editableDetails.diningPrice || ""}
                          onChange={handleDetailChange}
                          placeholder="Dining Price"
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* ROOM ORDER PRICE */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Room Order Price
                        </label>
                        <input
                          name="roomOrder"
                          value={editableDetails.roomOrder || ""}
                          onChange={handleDetailChange}
                          placeholder="Room Order Price"
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* CAR ORDER PRICE */}
                      <div>
                        <label className="text-sm font-medium text-gray-800">
                          Car Order Price
                        </label>
                        <input
                          name="carOrder"
                          value={editableDetails.carOrder || ""}
                          onChange={handleDetailChange}
                          placeholder="Car Order Price"
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>

                      {/* VARIANT INFO */}
                      <div className="md:col-span-2 xl:col-span-3">
                        <label className="text-sm font-medium text-gray-800">
                          Variant Info
                        </label>
                        <input
                          name="variantName"
                          value={
                            editableDetails.variantName
                              ? `${editableDetails.variantName} ${
                                  editableDetails.variantId
                                    ? `(ID: ${editableDetails.variantId})`
                                    : ""
                                }`
                              : ""
                          }
                          disabled
                          placeholder="Variant Info"
                          className="mt-1 w-full rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-600"
                        />
                      </div>
                    </div>

                    <div className="mt-5 flex justify-end">
                      <button
                        onClick={handleSaveCatalog}
                        disabled={savingCatalog}
                        className="rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-gray-300"
                      >
                        {savingCatalog ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* ALL ROWS INSIDE EDIT MODE */}
                {Array.isArray(viewModalItem.prices) &&
                  viewModalItem.prices.length > 0 && (
                    <div className="mt-6 montserrat-font-1">
                      <div className="mb-3 flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            All Size / Variant Rows
                          </h4>
                          <p className="mt-1 text-xs text-gray-500">
                            Select another row without closing edit mode.
                          </p>
                        </div>

                        <span className="text-xs text-gray-500">
                          {viewModalItem.prices.length} rows
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {viewModalItem.prices.map((p, i) => {
                          const isActive = i === activePriceIndex;

                          return (
                            <div
                              key={i}
                              className={`rounded-2xl border p-4 text-sm shadow-sm transition ${
                                isActive
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 bg-white hover:border-blue-200"
                              }`}
                            >
                              <div className="mb-3 flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {p.size || p.variantName || `Row ${i + 1}`}
                                  </div>

                                  <div className="mt-1 text-xs text-gray-500">
                                    Variant: {p.variantName || p.size || "-"}
                                  </div>
                                </div>

                                <div className="flex flex-col items-end gap-2">
                                  <span className="rounded-full border bg-white px-2 py-1 text-xs text-gray-700">
                                    {p.item_tax ?? "-"}% tax
                                  </span>

                                  {isActive && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2 py-1 text-xs font-semibold text-white">
                                      <CheckCircle2 size={12} />
                                      Active
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="space-y-2">
                                {[
                                  ["Selling", p.price],
                                  ["Home Delivery", p.home_delivery_price],
                                  ["Takeaway", p.takeaway_price],
                                  ["Dining", p.dining_price],
                                  ["Room Order", p.room_order_price],
                                  ["Car Order", p.car_order_price],
                                ].map(([label, value]) => (
                                  <div
                                    key={label}
                                    className="flex items-center justify-between border-b pb-1 last:border-b-0 last:pb-0"
                                  >
                                    <span className="text-gray-500">
                                      {label}
                                    </span>
                                    <span className="font-semibold text-gray-900">
                                      {value ? `₹${value}` : "-"}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              <button
                                onClick={() => {
                                  setActivePriceIndex(i);
                                  syncEditableFromPriceRow(
                                    i,
                                    viewModalItem.prices,
                                    viewModalItem,
                                  );
                                }}
                                className={`mt-4 w-full rounded-lg px-3 py-2 text-sm font-medium transition ${
                                  isActive
                                    ? "bg-blue-600 text-white"
                                    : "border border-blue-500 text-blue-600 hover:bg-blue-50"
                                }`}
                              >
                                {isActive
                                  ? "Currently Editing"
                                  : "Edit This Row"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {/* ================= AVAILABILITY ================= */}
            {viewModalTab === "availability" && (
              <div className="rounded-2xl border bg-white p-6 montserrat-font-1">
                <p className="mb-6 text-sm text-gray-600 montserrat-font-2">
                  Configure availability for this item:
                </p>

                <div className="space-y-4">
                  <label className="flex items-center gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={availability.includes("home_delivery")}
                      onChange={() => handleAvailabilityChange("home_delivery")}
                    />
                    <span>Home Delivery</span>
                  </label>

                  <label className="flex items-center gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={availability.includes("takeaway")}
                      onChange={() => handleAvailabilityChange("takeaway")}
                    />
                    <span>Takeaway</span>
                  </label>

                  <label className="flex items-center gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={availability.includes("dining")}
                      onChange={() => handleAvailabilityChange("dining")}
                    />
                    <span>Dine-in</span>
                  </label>
                </div>

                <button
                  onClick={handleSaveAvailability}
                  className="mt-6 w-full rounded-lg bg-emerald-600 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Save Availability
                </button>
              </div>
            )}

            {/* ================= PRINTER ================= */}
            {viewModalTab === "printer" && (
              <div className="rounded-2xl border bg-white p-6 montserrat-font-1">
                <p className="mb-5 text-sm text-gray-600 montserrat-font-2">
                  Assign a printer to this item. This ensures the order slip is
                  printed automatically for the selected section.
                </p>

                <select
                  value={selectedPrinter || ""}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                >
                  <option value="">Select Printer</option>

                  {(printerList || []).map((printer) => (
                    <option key={printer.id} value={printer.id}>
                      {printer.printerAlias || "No Name"} (
                      {printer.printerName || "N/A"})
                    </option>
                  ))}
                </select>

                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleSavePrinter}
                    className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600"
                  >
                    Save Printer
                  </button>

                  <button
                    onClick={() => setShowPrinterModal(true)}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Add Printer
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="flex justify-between border-t px-6 py-4">
            <button
              onClick={() => setViewModalItem(null)}
              className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>

            <button
              onClick={() => {
                navigate("/orders/catalogue/edit-ingredients", {
                  state: { item: viewModalItem },
                });
              }}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Ingredients Edit
            </button>
          </div>
        </div>

        {/* ADD PRINTER MODAL */}
        {showPrinterModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 montserrat-font-1">
            <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <button
                onClick={() => setShowPrinterModal(false)}
                className="absolute right-3 top-3 text-lg text-gray-500 hover:text-black"
              >
                ✕
              </button>

              <h2 className="mb-4 text-lg font-semibold text-gray-900">
                Add New Printer
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Printer Name</label>
                  <input
                    name="printerName"
                    value={printerForm.printerName}
                    onChange={handlePrinterChange}
                    className="mt-1 w-full rounded-lg border px-3 py-2"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600">Printer Alias</label>
                  <input
                    name="printerAlias"
                    value={printerForm.printerAlias}
                    onChange={handlePrinterChange}
                    className="mt-1 w-full rounded-lg border px-3 py-2"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600">Device Name</label>
                  <input
                    name="deviceName"
                    value={printerForm.deviceName}
                    onChange={handlePrinterChange}
                    className="mt-1 w-full rounded-lg border px-3 py-2"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600">Device No.</label>
                  <input
                    name="deviceNumber"
                    value={printerForm.deviceNumber}
                    onChange={handlePrinterChange}
                    className="mt-1 w-full rounded-lg border px-3 py-2"
                  />
                </div>

                <button
                  onClick={handleSaveNewPrinter}
                  className="w-full rounded-lg bg-green-500 py-2 font-semibold text-white hover:bg-green-600"
                >
                  Save Printer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
